interface Task {
  category: string
  items: string[]
}

interface Season {
  name: string
  months: string[]
  image: string
  tasks: Task[]
}

interface MaintenanceScheduleTranslations {
  title: string
  subtitle: string
  seasons: Season[]
  cta: {
    text: string
    button: string
  }
}

export const maintenanceScheduleTranslations: Record<string, MaintenanceScheduleTranslations> = {
  en: {
    title: 'Garden Maintenance Schedule',
    subtitle: 'Keep your garden looking its best year-round with our seasonal maintenance guide',
    seasons: [
      {
        name: 'Spring',
        months: ['March', 'April', 'May'],
        image: '/images/seasons/spring.webp',
        tasks: [
          {
            category: 'Lawn Care',
            items: [
              'Rake and remove thatch',
              'Apply spring fertilizer',
              'Overseed bare patches',
              'First mow of the season'
            ]
          },
          {
            category: 'Planting',
            items: [
              'Plant summer bulbs',
              'Sow annual seeds',
              'Plant new perennials',
              'Divide existing perennials'
            ]
          },
          {
            category: 'Pruning',
            items: [
              'Prune winter damage',
              'Shape hedges',
              'Trim roses',
              'Cut back ornamental grasses'
            ]
          }
        ]
      },
      {
        name: 'Summer',
        months: ['June', 'July', 'August'],
        image: '/images/seasons/summer.webp',
        tasks: [
          {
            category: 'Lawn Care',
            items: [
              'Regular mowing',
              'Water during dry spells',
              'Treat weeds',
              'Monitor for pests'
            ]
          },
          {
            category: 'Planting',
            items: [
              'Deadhead flowers',
              'Top up mulch to retain moisture',
              'Harvest vegetables',
              'Propagate from cuttings'
            ]
          },
          {
            category: 'Maintenance',
            items: [
              'Water containers daily',
              'Check irrigation systems',
              'Mulch beds',
              'Control pests'
            ]
          }
        ]
      },
      {
        name: 'Autumn',
        months: ['September', 'October', 'November'],
        image: '/images/seasons/autumn.webp',
        tasks: [
          {
            category: 'Lawn Care',
            items: [
              'Rake leaves',
              'Apply autumn fertilizer',
              'Aerate lawn',
              'Last mow of season'
            ]
          },
          {
            category: 'Planting',
            items: [
              'Plant spring bulbs',
              'Sow hardy greens like winter lettuce',
              'Plant trees and shrubs',
              'Collect seeds'
            ]
          },
          {
            category: 'Preparation',
            items: [
              'Clean garden tools',
              'Protect tender plants',
              'Drain irrigation systems',
              'Store garden furniture'
            ]
          }
        ]
      },
      {
        name: 'Winter',
        months: ['December', 'January', 'February'],
        image: '/images/seasons/winter.webp',
        tasks: [
          {
            category: 'Protection',
            items: [
              'Protect plants from frost',
              'Check tree ties',
              'Clear snow from branches',
              'Monitor for damage'
            ]
          },
          {
            category: 'Planning',
            items: [
              'Order seeds and plants',
              'Plan spring garden',
              'Review garden layout',
              'Research new plants'
            ]
          },
          {
            category: 'Maintenance',
            items: [
              'Prune deciduous trees like apple and pear',
              'Clean and sharpen tools',
              'Check stored bulbs',
              'Maintain paths'
            ]
          }
        ]
      }
    ],
    cta: {
      text: 'Need help with your garden maintenance? I\'m here to help!',
      button: 'Book a Consultation'
    }
  },
  cy: {
    title: 'Amserlen Cynnal a Chadw Gardd',
    subtitle: "Cadwch eich gardd yn edrych yn ei gorau trwy gydol y flwyddyn gyda fy nghanllaw cynnal a chadw tymhorol",
    seasons: [
      {
        name: 'Gwanwyn',
        months: ['Mawrth', 'Ebrill', 'Mai'],
        image: '/images/seasons/spring.webp',
        tasks: [
          {
            category: 'Gofal Lawnt',
            items: [
              'Rhaclo a thynnu gwair sych',
              'Rhoi gwrtaith gwanwyn',
              'Hadau ar dyllau moel',
              'Torri cyntaf y tymor'
            ]
          },
          {
            category: 'Plannu',
            items: [
              'Plannu bwlbau haf',
              'Hau hadau blynyddol',
              'Plannu perennau newydd',
              'Rhannu perennau presennol'
            ]
          },
          {
            category: 'Tocio',
            items: [
              'Tocio difrod gaeaf',
              'Siapio perthi',
              'Tocio rhosod',
              'Torri gwair addurnol yn ôl'
            ]
          }
        ]
      },
      {
        name: 'Haf',
        months: ['Mehefin', 'Gorffennaf', 'Awst'],
        image: '/images/seasons/summer.webp',
        tasks: [
          {
            category: 'Gofal Lawnt',
            items: [
              'Torri rheolaidd',
              'Dyfrio yn ystod cyfnodau sych',
              'Trin chwyn',
              'Monitro am blâu'
            ]
          },
          {
            category: 'Plannu',
            items: [
              'Tocio blodau',
              "Ychwanegu mwy o domwellt i gadw lleithder",
              'Cynaeafu llysiau',
              'Lluosogi o doriadau'
            ]
          },
          {
            category: 'Cynnal a Chadw',
            items: [
              'Dyfrio cynwysyddion yn ddyddiol',
              'Gwirio systemau dyfrhau',
              'Mulchio gwelyau',
              'Rheoli plâu'
            ]
          }
        ]
      },
      {
        name: 'Hydref',
        months: ['Medi', 'Hydref', 'Tachwedd'],
        image: '/images/seasons/autumn.webp',
        tasks: [
          {
            category: 'Gofal Lawnt',
            items: [
              'Rhaclo dail',
              'Rhoi gwrtaith hydref',
              'Aerio lawnt',
              'Torri olaf y tymor'
            ]
          },
          {
            category: 'Plannu',
            items: [
              'Plannu bylbiau gwanwyn',
              "Hau llysiau gwyrdd gwydn fel letys gaeaf",
              'Plannu coed a llwyni',
              'Casglu hadau'
            ]
          },
          {
            category: 'Paratoi',
            items: [
              'Glanhau offer gardd',
              'Amddiffyn planhigion tyner',
              'Gwagio systemau dyfrhau',
              'Storio dodrefn gardd'
            ]
          }
        ]
      },
      {
        name: 'Gaeaf',
        months: ['Rhagfyr', 'Ionawr', 'Chwefror'],
        image: '/images/seasons/winter.webp',
        tasks: [
          {
            category: 'Amddiffyn',
            items: [
              'Amddiffyn planhigion rhag rhew',
              'Gwirio rhwymau coed',
              'Clirio eira o ganghennau',
              'Monitro am ddifrod'
            ]
          },
          {
            category: 'Cynllunio',
            items: [
              'Archebu hadau a phlanhigion',
              'Cynllunio gardd gwanwyn',
              'Adolygu cynllun gardd',
              'Ymchwilio i blanhigion newydd'
            ]
          },
          {
            category: 'Cynnal a Chadw',
            items: [
              "Tocio coed collddail fel afalau a gellyg",
              'Glanhau a miniogi offer',
              'Gwirio bylbiau sydd wedi eu storio',
              'Cynnal a chadw llwybrau'
            ]
          }
        ]
      }
    ],
    cta: {
      text: 'Angen help gyda chynnal a chadw eich gardd? Mae fy nghriw arbenigol yma i helpu!',
      button: 'Archebu Ymgynghoriad'
    }
  }
} 